'use client';

import React from 'react';

type BaseProps = { children: React.ReactNode; className?: string; disabled?: boolean; style?: React.CSSProperties; };
type AsButton = BaseProps & React.ButtonHTMLAttributes<HTMLButtonElement> & { href?: undefined };
type AsAnchor = BaseProps & React.AnchorHTMLAttributes<HTMLAnchorElement> & { href: string };
type SecondaryButtonProps = AsButton | AsAnchor;

const BTN_STYLE: React.CSSProperties = {
  fontFamily: 'var(--font-poppins), Poppins, sans-serif',
  boxShadow: '0 3px 2px 0 rgba(250, 77, 117, 0.15)',
  border: '2px solid transparent',
  background:
    'linear-gradient(white, white) padding-box, ' +
    'linear-gradient(to right, #F52F81 0%, #FD7468 100%) border-box',
  borderRadius: '20px',
};

const TEXT_STYLE: React.CSSProperties = {
  background: 'linear-gradient(to right, #F52F81 0%, #FD7468 100%)',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
};

export default function SecondaryButton({
  children,
  className = '',
  disabled,
  style,
  ...props
}: SecondaryButtonProps) {
  const sharedClassName = [
    'group relative overflow-hidden',
    'inline-flex justify-center items-center gap-[10px]',
    'py-[13px] px-6',
    'text-[18px] font-semibold',
    disabled ? 'opacity-50 cursor-not-allowed pointer-events-none' : 'hover:brightness-[0.9] hover:scale-[0.98] transition-all duration-200 ease-in-out',
    className,
  ].join(' ');

  const sharedStyle = { ...BTN_STYLE, ...style };

  const overlay = !disabled ? (
    <span
      className="absolute inset-0 opacity-0 group-hover:opacity-100"
      style={{ background: 'rgba(0,0,0,0.1)', transition: 'none', borderRadius: 'inherit' }}
      aria-hidden="true"
    />
  ) : null;

  if ('href' in props && props.href !== undefined) {
    const { href, ...anchorProps } = props as AsAnchor;
    return (
      <a href={href} {...anchorProps} className={sharedClassName} style={sharedStyle}>
        {overlay}
        <span className="relative whitespace-nowrap" style={TEXT_STYLE}>{children}</span>
      </a>
    );
  }

  return (
    <button {...(props as AsButton)} disabled={disabled} className={sharedClassName} style={sharedStyle}>
      {overlay}
      <span className="relative whitespace-nowrap" style={TEXT_STYLE}>{children}</span>
    </button>
  );
}
